import { decryptField, encryptField } from '../crypto/aesgcm';
import { bytesToBase64Url, randomBytes } from '../crypto/encoding';
import type {
  CruzFindVault,
  DocumentType,
  InvoiceSequenceRow,
  LedgerStatus,
  SalesLedgerRow,
} from './schema';

export interface NextSerials {
  nextInvoiceNumber: number;
  nextBillingNumber: number;
}

export interface SalesLedgerEntry {
  id: string;
  sequenceKey: string;
  serialString: string;
  documentType: DocumentType;
  timestamp: number;
  payload: unknown;
  status: LedgerStatus;
}

export interface IssueInvoiceArgs {
  sequenceKey: string;
  documentType: DocumentType;
  /** Branch code for SALES_INVOICE serial composition. Ignored for BILLING_STATEMENT. */
  branchCode?: string;
  terminalId?: string;
  payload: unknown;
  status?: LedgerStatus;
}

export class LedgerRecordNotFoundError extends Error {
  constructor(id: string) {
    super(`Ledger record not found: ${id}`);
    this.name = 'LedgerRecordNotFoundError';
  }
}

const INVOICE_SERIAL_PADDING = 7;
const BILLING_SERIAL_PADDING = 6;
const COMPONENT_RE = /^[A-Z0-9]{1,8}$/;

export function formatInvoiceSerial(
  branchCode: string,
  terminalId: string,
  counter: number,
): string {
  if (!COMPONENT_RE.test(branchCode))
    throw new Error(`Invalid branchCode: ${JSON.stringify(branchCode)}`);
  if (!COMPONENT_RE.test(terminalId))
    throw new Error(`Invalid terminalId: ${JSON.stringify(terminalId)}`);
  return `BR-${branchCode}-${terminalId}-${String(counter).padStart(INVOICE_SERIAL_PADDING, '0')}`;
}

export function formatBillingSerial(counter: number): string {
  return `BS-${String(counter).padStart(BILLING_SERIAL_PADDING, '0')}`;
}

function newLedgerId(): string {
  return bytesToBase64Url(randomBytes(16));
}

export class SalesLedgerRepo {
  constructor(
    private readonly db: CruzFindVault,
    private readonly aesKey: CryptoKey,
  ) {}

  async peekNextSerials(sequenceKey: string): Promise<NextSerials> {
    const row = await this.db.invoice_sequences.get(sequenceKey);
    return {
      nextInvoiceNumber: row?.nextInvoiceNumber ?? 1,
      nextBillingNumber: row?.nextBillingNumber ?? 1,
    };
  }

  async list(sequenceKey: string): Promise<SalesLedgerEntry[]> {
    const rows = await this.db.sales_ledger
      .where('sequenceKey')
      .equals(sequenceKey)
      .sortBy('timestamp');
    return Promise.all(rows.map((r) => this.decryptRow(r)));
  }

  /**
   * Atomically allocate the next serial for `documentType` and append a ledger row.
   * The sequence increment and ledger insert run inside a single Dexie rw transaction —
   * if either fails the entire operation rolls back, guaranteeing no serial gaps.
   */
  async saveInvoiceAndIncrementSerial(args: IssueInvoiceArgs): Promise<SalesLedgerEntry> {
    const payloadJson = JSON.stringify(args.payload);
    const encryptedPayload = await encryptField(this.aesKey, payloadJson);

    return this.db.transaction(
      'rw',
      this.db.invoice_sequences,
      this.db.sales_ledger,
      async () => {
        const now = Date.now();
        const existing = await this.db.invoice_sequences.get(args.sequenceKey);
        const current: InvoiceSequenceRow = existing ?? {
          sequenceKey: args.sequenceKey,
          nextInvoiceNumber: 1,
          nextBillingNumber: 1,
          updatedAt: now,
        };

        let serialString: string;
        let nextSeq: InvoiceSequenceRow;
        if (args.documentType === 'SALES_INVOICE') {
          serialString = formatInvoiceSerial(
            args.branchCode ?? '001',
            args.terminalId ?? 'A',
            current.nextInvoiceNumber,
          );
          nextSeq = {
            ...current,
            nextInvoiceNumber: current.nextInvoiceNumber + 1,
            updatedAt: now,
          };
        } else {
          serialString = formatBillingSerial(current.nextBillingNumber);
          nextSeq = {
            ...current,
            nextBillingNumber: current.nextBillingNumber + 1,
            updatedAt: now,
          };
        }

        const row: SalesLedgerRow = {
          id: newLedgerId(),
          sequenceKey: args.sequenceKey,
          serialString,
          documentType: args.documentType,
          timestamp: now,
          encryptedPayload,
          status: args.status ?? 'draft',
        };

        await this.db.sales_ledger.add(row);
        await this.db.invoice_sequences.put(nextSeq);

        return {
          id: row.id,
          sequenceKey: row.sequenceKey,
          serialString: row.serialString,
          documentType: row.documentType,
          timestamp: row.timestamp,
          payload: args.payload,
          status: row.status,
        };
      },
    );
  }

  /**
   * Cancel a document by flipping `status` to 'voided' and nullifying balances.
   * Records are never deleted — this is the only mutation path exposed on the ledger.
   */
  async cancelDocument(id: string): Promise<SalesLedgerEntry> {
    return this.db.transaction('rw', this.db.sales_ledger, async () => {
      const row = await this.db.sales_ledger.get(id);
      if (!row) throw new LedgerRecordNotFoundError(id);
      if (row.status === 'voided') return this.decryptRow(row);

      const payloadJson = await decryptField(this.aesKey, row.encryptedPayload);
      let payload: any;
      try {
        payload = JSON.parse(payloadJson);
      } catch {
        payload = {};
      }

      // Mutate financial balances to 0 for BIR compliance (RMC 77-2024)
      payload.grossSales = 0;
      payload.vatComponent = 0;
      if (payload.lines && Array.isArray(payload.lines)) {
        payload.lines = payload.lines.map((line: any) => ({
          ...line,
          quantity: 0,
          unitPrice: 0,
        }));
      } else {
        payload.lines = [];
      }

      const encryptedPayload = await encryptField(this.aesKey, JSON.stringify(payload));
      const updated: SalesLedgerRow = {
        ...row,
        status: 'voided',
        encryptedPayload,
      };

      await this.db.sales_ledger.put(updated);
      return this.decryptRow(updated);
    });
  }

  private async decryptRow(row: SalesLedgerRow): Promise<SalesLedgerEntry> {
    const payloadJson = await decryptField(this.aesKey, row.encryptedPayload);
    let payload: unknown;
    try {
      payload = JSON.parse(payloadJson);
    } catch {
      payload = payloadJson;
    }
    return {
      id: row.id,
      sequenceKey: row.sequenceKey,
      serialString: row.serialString,
      documentType: row.documentType,
      timestamp: row.timestamp,
      payload,
      status: row.status,
    };
  }
}
